import React, { useRef, useState } from 'react'

interface PhotoUploadProps {
  onFileChange: (file: File | null) => void
  isUploading: boolean
  error: string | null
}

export function PhotoUpload({ onFileChange, isUploading, error }: PhotoUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

  function handleFile(file: File) {
    setLocalError(null)
    if (!ALLOWED_TYPES.includes(file.type)) {
      setLocalError('Only JPEG, PNG, and WEBP images are supported.')
      return
    }
    setSelectedFile(file)
    try {
      setPreview(URL.createObjectURL(file))
    } catch {
      setPreview(null)
    }
    onFileChange(file)
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  function handleRemove() {
    setSelectedFile(null)
    setPreview(null)
    setLocalError(null)
    onFileChange(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const displayError = localError ?? error

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-stone-300">
        Photo{' '}
        <span className="font-normal text-stone-500">(optional)</span>
      </p>

      {selectedFile ? (
        <div className="relative w-full overflow-hidden rounded-md border border-stone-700">
          {preview ? (
            <img
              src={preview}
              alt="Selected photo preview"
              className="max-h-64 w-full object-contain bg-stone-900"
            />
          ) : (
            <div className="flex items-center gap-3 bg-stone-800/40 px-4 py-5">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                className="h-6 w-6 shrink-0 text-stone-400"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                />
              </svg>
              <span className="truncate text-sm text-stone-300">{selectedFile.name}</span>
            </div>
          )}

          {isUploading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-stone-900/70">
              <div className="flex flex-col items-center gap-2">
                <svg
                  className="h-8 w-8 animate-spin text-red-700"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
                <span className="text-xs text-stone-300">Uploading photo…</span>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleRemove}
              className="absolute right-2 top-2 rounded-full bg-stone-900/80 p-1.5 text-stone-300 hover:text-stone-100 transition-colors"
              aria-label="Remove photo"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={`w-full rounded-md border-2 border-dashed px-4 py-8 text-center transition-colors ${
            dragging
              ? 'border-red-700 bg-red-950/20'
              : 'border-stone-700 bg-stone-800/40 hover:border-stone-500 hover:bg-stone-800/60'
          }`}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            className="mx-auto mb-3 h-8 w-8 text-stone-500"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
            />
          </svg>
          <p className="text-sm text-stone-400">
            Add a photo — playbill, programme, or anything that captures the memory
          </p>
          <p className="mt-1 text-xs text-stone-600">
            JPEG, PNG, or WEBP · drag and drop or click to browse
          </p>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleInputChange}
        className="hidden"
        aria-label="Upload photo"
      />

      {displayError && (
        <p className="text-sm text-red-400">{displayError}</p>
      )}
    </div>
  )
}
